import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '@/lib/api';
import TopBar from '@/components/TopBar';
import { Label, Card, Select, Stepper, CardSm, Pill, Textarea } from '@/components/ui/Form';
import { Button } from '@/components/ui/Button';
import { fmt, fileToBase64 } from '@/lib/utils';
import { Camera, Plus, X, CheckCircle, Sparkle, Copy } from '@phosphor-icons/react';

const PHOTO_CATEGORIES = [
  { v: 'full_tree', label: 'Full tree' },
  { v: 'dbh_point', label: 'DBH point' },
  { v: 'canopy', label: 'Canopy' },
  { v: 'foliage', label: 'Foliage' },
];

function estimateBiomass(dbhArr, rho = 0.75) {
  if (!dbhArr || !dbhArr.length) return { agb: 0, bgb: 0, tCO2e: 0 };
  let agb = 0, bgb = 0;
  for (const d of dbhArr) {
    if (!d || d <= 0) continue;
    agb += 0.251 * rho * Math.pow(d, 2.46);
    bgb += 0.199 * Math.pow(rho, 0.899) * Math.pow(d, 2.22);
  }
  const tCO2e = ((agb + bgb) * 0.47 * 3.667) / 1000;
  return { agb, bgb, tCO2e };
}

export default function MeasurementEntry() {
  const { treeId } = useParams();
  const nav = useNavigate();
  const [tree, setTree] = useState(null);
  const [dbh, setDbh] = useState([15]);
  const [height, setHeight] = useState(7);
  const [crownNS, setCrownNS] = useState(3);
  const [crownEW, setCrownEW] = useState(3);
  const [woodDensity, setWoodDensity] = useState(0.75);
  const [tidal, setTidal] = useState('mid');
  const [substrate, setSubstrate] = useState('mud');
  const [notes, setNotes] = useState('');
  const [photos, setPhotos] = useState([]); // [{photo_category, image_base64, mime_type}]
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [lastSiteEvent, setLastSiteEvent] = useState(null);

  useEffect(() => {
    api.get(`/trees/${treeId}`).then((r) => setTree(r.data));
  }, [treeId]);

  // Pull the most recent measurement (any tree) in this plot to offer a
  // one-tap "copy site context" — tidal zone + substrate + wood density.
  useEffect(() => {
    if (!tree?.plot_id) return;
    api.get('/measurements', { params: { plot_id: tree.plot_id } })
      .then(({ data }) => {
        const candidate = (data || []).find(
          (m) => m.tree_id !== treeId &&
                 (m.tidal_zone || m.substrate_type || m.wood_density_g_cm3)
        );
        setLastSiteEvent(candidate || null);
      })
      .catch(() => setLastSiteEvent(null));
  }, [tree?.plot_id, treeId]);

  const copySiteContext = () => {
    if (!lastSiteEvent) return;
    if (lastSiteEvent.tidal_zone) setTidal(lastSiteEvent.tidal_zone);
    if (lastSiteEvent.substrate_type) setSubstrate(lastSiteEvent.substrate_type);
    if (lastSiteEvent.wood_density_g_cm3) setWoodDensity(lastSiteEvent.wood_density_g_cm3);
  };

  // Most recent measurement for THIS tree (drives the "Copy DBH from previous"
  // shortcut on remeasurement runs). Already returned by /api/trees/{id}.
  const previousEvent = (tree?.measurement_events || []).find(
    (e) => e.dbh_cm && e.dbh_cm.length > 0
  );

  const copyPreviousMeasurement = () => {
    if (!previousEvent) return;
    if (previousEvent.dbh_cm?.length) setDbh([...previousEvent.dbh_cm]);
    if (previousEvent.height_total_m != null) setHeight(previousEvent.height_total_m);
    if (previousEvent.crown_width_ns_m != null) setCrownNS(previousEvent.crown_width_ns_m);
    if (previousEvent.crown_width_ew_m != null) setCrownEW(previousEvent.crown_width_ew_m);
    if (previousEvent.wood_density_g_cm3 != null) setWoodDensity(previousEvent.wood_density_g_cm3);
  };

  const addPhoto = async (category, file) => {
    const b64 = await fileToBase64(file);
    setPhotos((p) => [...p, { photo_category: category, image_base64: b64, mime_type: file.type || 'image/jpeg' }]);
  };

  const removePhoto = (i) => setPhotos((p) => p.filter((_, idx) => idx !== i));

  const bio = estimateBiomass(dbh, woodDensity);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setErr('');
    try {
      await api.post('/measurements', {
        tree_id: treeId,
        event_type: previousEvent ? 'remeasurement' : 'initial',
        dbh_cm: dbh.filter((d) => d > 0),
        height_total_m: Number(height),
        crown_width_ns_m: Number(crownNS),
        crown_width_ew_m: Number(crownEW),
        wood_density_g_cm3: Number(woodDensity),
        tidal_zone: tidal,
        substrate_type: substrate,
        notes_text: notes || undefined,
        media: photos,
      });
      nav(`/trees/${treeId}/health`);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  if (!tree) return <div className="p-4">Loading…</div>;

  return (
    <div data-testid="measurement-entry">
      <TopBar title="Measurement" back />
      <form onSubmit={save} className="p-4 space-y-4 pb-24">
        <CardSm>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-display font-bold">{tree.tree_code}</p>
              <p className="text-xs mono text-carbon-soft italic">{tree.species_scientific || 'unidentified'}</p>
            </div>
            <Pill>{tree.tree_status}</Pill>
          </div>
        </CardSm>

        {/* DBH */}
        <Card data-testid="dbh-card">
          <div className="flex items-center justify-between mb-2">
            <Label className="mb-0">DBH @ 1.3 m (cm)</Label>
            <button
              type="button"
              onClick={() => setDbh([...dbh, 10])}
              className="text-xs font-display font-bold uppercase tracking-widest underline"
              data-testid="add-stem-btn"
            >
              + add stem
            </button>
          </div>

          {previousEvent && (
            <div
              data-testid="previous-measurement-ref"
              className="mb-3 border-2 border-carbon bg-surface-alt p-2 flex items-center justify-between gap-2"
            >
              <div className="min-w-0">
                <p className="text-[10px] mono uppercase tracking-widest text-carbon-soft">
                  Previous · {new Date(previousEvent.event_date).toLocaleDateString()}
                </p>
                <p className="mono text-xs truncate">
                  DBH <b>{(previousEvent.dbh_cm || []).join(', ')} cm</b>
                  {previousEvent.height_total_m != null && (
                    <> · H <b>{previousEvent.height_total_m} m</b></>
                  )}
                </p>
              </div>
              <button
                type="button"
                onClick={copyPreviousMeasurement}
                data-testid="copy-previous-measurement-btn"
                className="shrink-0 text-[11px] px-2 py-1 border-2 border-carbon bg-white hover:bg-canvas inline-flex items-center gap-1 font-display font-bold uppercase tracking-widest"
                title="Copy DBH + height + crown from previous measurement"
              >
                <Copy size={12} weight="bold" />
                Copy
              </button>
            </div>
          )}

          <div className="space-y-2">
            {dbh.map((d, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="mono text-xs w-10 text-carbon-soft">Stem {i + 1}</span>
                <div className="flex-1">
                  <Stepper
                    value={d}
                    step={0.1}
                    unit="cm"
                    onChange={(v) => setDbh(dbh.map((x, idx) => idx === i ? v : x))}
                    testid={`dbh-stem-${i}`}
                  />
                </div>
                {dbh.length > 1 && (
                  <button type="button" onClick={() => setDbh(dbh.filter((_, idx) => idx !== i))}
                          className="w-10 h-10 border-2 border-carbon bg-white" data-testid={`remove-stem-${i}`}>
                    <X size={18} weight="bold" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </Card>

        {/* Height */}
        <Card>
          <Label>Total height (m)</Label>
          <Stepper value={height} step={0.1} unit="m" onChange={setHeight} testid="height-stepper" />
        </Card>

        {/* Crown */}
        <Card>
          <Label>Crown width (m)</Label>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <p className="text-xs mono uppercase mb-1">N-S</p>
              <Stepper value={crownNS} step={0.1} unit="m" onChange={setCrownNS} testid="crown-ns" />
            </div>
            <div>
              <p className="text-xs mono uppercase mb-1">E-W</p>
              <Stepper value={crownEW} step={0.1} unit="m" onChange={setCrownEW} testid="crown-ew" />
            </div>
          </div>
        </Card>

        {/* Wood density */}
        <Card>
          <Label>Wood density (g/cm³)</Label>
          <Stepper value={woodDensity} step={0.01} unit="g/cm³" onChange={setWoodDensity} testid="wood-density" />
        </Card>

        {/* Site context */}
        <Card>
          <div className="flex items-center justify-between mb-2">
            <Label className="mb-0">Tidal zone</Label>
            {lastSiteEvent && (
              <button
                type="button"
                onClick={copySiteContext}
                data-testid="copy-site-context-btn"
                className="text-[11px] px-2 py-1 border-2 border-carbon bg-surface-alt hover:bg-white inline-flex items-center gap-1 font-display font-bold uppercase tracking-widest"
                title={`Copy tidal/substrate/wood-density from previous plot measurement (${lastSiteEvent.tidal_zone || '—'} / ${lastSiteEvent.substrate_type || '—'})`}
              >
                <Copy size={12} weight="bold" />
                Copy from last
              </button>
            )}
          </div>
          <div className="grid grid-cols-3 gap-2 mb-3" data-testid="tidal-pills">
            {['low', 'mid', 'high'].map((t) => (
              <button key={t} type="button" onClick={() => setTidal(t)}
                      data-testid={`tidal-${t}`}
                      className={`min-h-[44px] border-2 border-carbon text-xs font-display font-bold uppercase tracking-widest ${
                        tidal === t ? 'bg-carbon text-canvas' : 'bg-white hover:bg-surface-alt'
                      }`}>
                {t}
              </button>
            ))}
          </div>
          <Label>Substrate</Label>
          <Select value={substrate} onChange={(e) => setSubstrate(e.target.value)} data-testid="substrate-select">
            <option value="mud">Mud</option>
            <option value="sand">Sand</option>
            <option value="peat">Peat</option>
          </Select>
        </Card>

        {/* Biomass preview */}
        <Card data-testid="biomass-preview" className="!bg-carbon !text-canvas">
          <div className="flex items-center gap-2 mb-2 text-canvas/80">
            <Sparkle size={16} weight="bold" />
            <span className="font-display text-xs font-bold uppercase tracking-widest">Live Carbon Estimate</span>
          </div>
          <p className="mono text-3xl font-black leading-none">{fmt(bio.tCO2e, 4)} <span className="text-base font-normal">tCO₂e</span></p>
          <p className="mono text-xs mt-1 text-canvas/70">AGB {fmt(bio.agb, 1)} kg · BGB {fmt(bio.bgb, 1)} kg</p>
        </Card>

        {/* Photos */}
        <Card data-testid="photo-card">
          <Label>Photos (required for evidence)</Label>
          <div className="grid grid-cols-2 gap-2">
            {PHOTO_CATEGORIES.map((c) => {
              const has = photos.some((p) => p.photo_category === c.v);
              return (
                <label key={c.v}
                       className={`min-h-[80px] border-2 border-carbon flex flex-col items-center justify-center gap-1 cursor-pointer ${
                         has ? 'bg-status-healthy/15' : 'bg-surface-alt hover:bg-white'
                       }`}
                       data-testid={`photo-slot-${c.v}`}
                >
                  <input type="file" accept="image/*" capture="environment" className="hidden"
                         onChange={(e) => e.target.files?.[0] && addPhoto(c.v, e.target.files[0])} />
                  {has ? <CheckCircle size={24} weight="bold" /> : <Camera size={24} weight="bold" />}
                  <span className="font-display text-[11px] font-bold uppercase tracking-widest">{c.label}</span>
                </label>
              );
            })}
          </div>
          {photos.length > 0 && (
            <ul className="mt-3 space-y-1" data-testid="photo-list">
              {photos.map((p, i) => (
                <li key={i} className="flex items-center justify-between text-xs mono">
                  <span>📷 {p.photo_category}</span>
                  <button type="button" onClick={() => removePhoto(i)} className="font-bold">×</button>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* Notes */}
        <Card>
          <Label>Notes (optional)</Label>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any field observations…" data-testid="notes-input" />
        </Card>

        {err && <div className="border-2 border-status-severe bg-red-50 px-3 py-2 text-sm text-status-severe">{err}</div>}

        <Button type="submit" disabled={saving} size="lg" className="w-full" data-testid="save-measurement-btn">
          {saving ? 'Saving…' : 'Save & assess health →'}
        </Button>
      </form>
    </div>
  );
}
